/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import * as React from 'react';
import { ThemedButton } from '@helpcenter/themed-button';
import type { WrappedComponentProps } from 'react-intl-next';
import { FormattedMessage, injectIntl } from 'react-intl-next';
import { TextArea } from 'view/common/fields/text-area';
import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import messages from './messages';

export interface CommentProps {
    onSaveComment: (comment: string) => void;
    canSubmit: boolean;
    isSubmitting: boolean;
}

interface State {
    comment: string;
}

const maxCommentLength = 1000;

export class Comment extends React.Component<CommentProps & WrappedComponentProps, State> {
    constructor(props: CommentProps & WrappedComponentProps) {
        super(props);

        this.state = {
            comment: '',
        };
    }

    onCommentBoxChange = (_: string, updatedComment: string) => {
        this.setState({
            comment: updatedComment,
        });
    };

    transformComment = (comment: string) => {
        if (!comment) {
            return comment;
        }
        const trimmedComment = comment.trim();
        if (trimmedComment.length > maxCommentLength) {
            return `${trimmedComment.substring(0, maxCommentLength - 3)}...`;
        }
        return trimmedComment;
    };

    onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        const { onSaveComment } = this.props;
        const comment = this.state.comment;
        if (comment) {
            onSaveComment(this.transformComment(comment));
        }
    };

    isTextAreaDisabled = () => {
        const { isSubmitting, canSubmit } = this.props;
        return isSubmitting || !canSubmit;
    };

    isButtonDisabled = () => {
        return !this.props.canSubmit || !(this.state.comment.trim().length > 0);
    };

    render() {
        const { intl, isSubmitting } = this.props;
        const { comment } = this.state;
        return (
            <div css={commentContainerStyles}>
                <form id="comment-form" onSubmit={this.onSubmit}>
                    <div css={textAreaContainerStyles}>
                        <TextArea
                            label={intl.formatMessage(messages.commentPlaceholder)}
                            isLabelHidden
                            id="request-feedback-comment"
                            minimumRows={7}
                            onChange={this.onCommentBoxChange}
                            maxLength={maxCommentLength}
                            placeholder={intl.formatMessage(messages.commentPlaceholder)}
                            disabled={this.isTextAreaDisabled()}
                            enableResize={false}
                            defaultValue={comment}
                        />
                    </div>
                    <ThemedButton
                        id="add-comment-button"
                        type="submit"
                        appearance="primary"
                        isDisabled={this.isButtonDisabled()}
                        isLoading={isSubmitting}
                        actionSubjectId="addComment"
                    >
                        <FormattedMessage {...messages.addComment} />
                    </ThemedButton>
                </form>
            </div>
        );
    }
}

export default injectIntl(Comment);

const commentContainerStyles = css({
    maxWidth: '352px',
    textAlign: 'center',
});

const textAreaContainerStyles = css({
    marginBottom: token('space.300', '24px'),
});
